import { FC, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { CollectionsViewPageHeader } from "@/pages/collections-view/components/collections-view-page-header";
import { CollectionsViewPageList } from "@/pages/collections-view/components/collections-view-page-list";
import { DeleteCollectionsModal } from "@/components/modal/delete-collections";
import { DeleteSharedNotesModal } from "@/components/modal/delete-shared-notes";
import { ShareSheetModal } from "@/components/modal/share-sheet/share-sheet-modal";
import { RemoveNotesFromSharedCollectionModal } from "@/components/modal/remove-notes-from-shared-collection";
import { MakeCollectionPrivateModal } from "@/components/modal/make-collection-private";
import { MDSPanelContainer, AppearAnimator } from "@/mds";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import styled from "@emotion/styled";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import { EntityNotFoundType, NotFoundPage } from "@/pages/not-found/not-found-page";

interface CollectionsViewPageProps {
  isSidePanel?: boolean;
}

export const CollectionsViewPage: FC<CollectionsViewPageProps> = observer(
  ({ isSidePanel = false }) => {
    const pageViewStore = useTabPageStore("collectionsViewPage");
    pageViewStore.useEffects();

    /**
     * Track views of the collection.
     */
    useEffect(() => {
      if (!pageViewStore.collection) return;
      trackEvent(TrackedEvent.CollectionView, {
        collection_id: pageViewStore.collection.id,
        collection_primary_label: pageViewStore.collection.label,
        is_in_side_panel: isSidePanel,
      });
    }, [pageViewStore.collection, isSidePanel]);

    if (pageViewStore.showNotFound) {
      return <NotFoundPage entityType={EntityNotFoundType.Collection} />;
    }

    return (
      <StyledAppear visible={pageViewStore.isSubscriptionInitialized}>
        <MakeCollectionPrivateModal store={pageViewStore.makeCollectionPrivateModal} />
        <ShareSheetModal store={pageViewStore.shareSheetModal} />
        <DeleteCollectionsModal state={pageViewStore.deleteCollectionsModal} />
        <DeleteSharedNotesModal store={pageViewStore.deleteSharedNotesModal} />
        <RemoveNotesFromSharedCollectionModal
          store={pageViewStore.removeNotesFromSharedCollectionModal}
        />
        <CollectionsViewPageHeader />
        <MDSPanelContainer>
          <CollectionsViewPageList />
        </MDSPanelContainer>
      </StyledAppear>
    );
  }
);

const StyledAppear = styled(AppearAnimator)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  overflow: "hidden",
  height: "100%",
});
